import { type TiersConfig } from "@clipboard-health/flag-attendance-policy";
import { type IconType } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type Tier } from "@src/appV2/redesign/Tiers/api/schema";
import { useGetTiers } from "@src/appV2/redesign/Tiers/api/useGetTiers";
import { buildNormalizedTierRatings } from "@src/appV2/redesign/Tiers/utils/buildNormalizedTierRatings";
import { TIER_ICONS } from "@src/appV2/redesign/Tiers/utils/constants";
import { type NormalizedTierRatings } from "@src/appV2/redesign/Tiers/utils/types";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

type UseTierResponse =
  | { successfullyLoadedTier: false; enabled: boolean; isLoading: boolean }
  | { successfullyLoadedTier: boolean; enabled: false; isLoading: boolean }
  | {
      successfullyLoadedTier: true;
      enabled: true;
      isLoading: false;
      tier: Tier;
      tierIcon: IconType;
      tierConfiguration: Extract<TiersConfig, { enabled: true }>;
      normalizedTierRatings: NormalizedTierRatings;
      workerTimeZone: string;
    };

export function useTier(): UseTierResponse {
  const worker = useDefinedWorker();
  const tierConfiguration = useCbhFlag(CbhFeatureFlag.CONFIG_TIERS, {
    defaultValue: { enabled: false },
  });
  const { enabled } = tierConfiguration;
  const {
    data: tiers,
    isSuccess,
    isLoading,
  } = useGetTiers(
    { workerIds: [worker?.userId] },
    { enabled: isDefined(worker?.userId) && enabled }
  );
  const successfullyLoadedTier = isSuccess && tiers.data.length > 0;
  if (!successfullyLoadedTier) {
    return { successfullyLoadedTier, isLoading, enabled };
  }

  if (!enabled) {
    return { successfullyLoadedTier, isLoading, enabled };
  }

  const normalizedTierRatings: NormalizedTierRatings = buildNormalizedTierRatings(
    tiers.data[0],
    tierConfiguration
  );

  return {
    successfullyLoadedTier,
    isLoading,
    enabled,
    tierConfiguration,
    tier: tiers.data[0],
    normalizedTierRatings,
    tierIcon: TIER_ICONS[tiers.data[0].attributes.type],
    workerTimeZone: worker.tmz ?? DEFAULT_TIMEZONE,
  };
}
